import { deserialize, DocumentObject, serialize } from '@jsonapiworld/jsonapi-serializer';
import { create, keyResolver, windowedFiniteBatchScheduler } from '@yornaath/batshit';
import { addDays, parseISO } from 'date-fns';
import isEmpty from 'lodash/isEmpty';
import { backendApiGateway } from '~/client/backend';
import { NO_ACCOUNT_VALUE } from '~/consts/account';
import { Company } from '~/types/company';
import {
  BulkUpdateFlowPayload,
  FetchFlowFilters,
  Flow,
  FlowCreate,
  FlowPaymentStatus,
  FlowResourceType,
  FlowsMeta,
  FlowSorting,
  FlowsTotals,
  FlowWithDocument,
  SplitPaymentMoveToExistingRequest,
  SplitPaymentNewFlowRequest,
  UpdateFlowValues,
} from '~/types/flow';
import { MetaDataCursorPage, pagedMetaSchema } from '~/types/jsonapi';
import { isDefined } from '~/types/typeGuards';
import { addCategoryFilter } from '~/utils/filters';
import { formatDateForAPI, formatISOForAPI } from '~/utils/formatDate';
import {
  buildJsonAPIQueryParams,
  type IJsonAPIFilterBuilder,
  jsonAPICursorPaginatorBuilder,
  jsonAPIFieldsBuilder,
  jsonAPIFilterBuilder,
  jsonAPIIncludeBuilder,
  jsonAPISortBuilder,
  type PaginationInfo,
  type SortInfo,
} from '~/utils/jsonapi-query';

const addDateRangeFilter = (
  filters: IJsonAPIFilterBuilder,
  field: string,
  dateRange: [string | null, string | null] | undefined,
) => {
  if (!dateRange) return;

  const [from, to] = dateRange;
  if (from) {
    const date = formatISOForAPI(from);
    filters.gte(field, date);
  }
  if (to) {
    const date = formatDateForAPI(addDays(parseISO(to), 1));
    filters.lt(field, date);
  }
};

export const fetchFlowFiltersTojsonAPI = (filter: FetchFlowFilters) => {
  const filters = jsonAPIFilterBuilder();
  if (filter.search) {
    filters.iLikeAnd('search', filter.search.split(' '));
  }
  if (filter.companyId) {
    filters.eq('company.id', filter.companyId);
  }
  if (filter.counterpartId) {
    filters.eq('document.counterpart.id', filter.counterpartId);
  }
  if (filter.customCounterpartId) {
    filters.eq('customCounterpartId', filter.customCounterpartId);
  }
  if (filter.isInflow === true || filter.isInflow === false) {
    filters.eq('document.isInflow', filter.isInflow);
  }
  if (filter.documentNumber) {
    filters.ilike('document.number', filter.documentNumber);
  }
  if (filter.documentDirection) {
    filters.eq('document.direction', filter.documentDirection);
  }
  if (filter.documentStatus) {
    filters.eq('document.status', filter.documentStatus);
  }
  if (filter.paymentStatus) {
    filters.eq('paymentStatus', filter.paymentStatus);
  }
  if (filter.extendedPaymentStatus) {
    filters.eq('extendedPaymentStatus', filter.extendedPaymentStatus);
  }
  if (filter.verificationStatus) {
    filters.eq('verificationStatus', filter.verificationStatus);
  }
  addDateRangeFilter(filters, 'expectedPaymentDate', filter.dateRange);
  addDateRangeFilter(filters, 'document.flowHasExpectedPaymentDate', filter.documentFlowExpectedPaymentDateRange);
  if (filter.paymentMethod && filter.paymentMethod.length > 0) {
    filters.in('paymentMethod', filter.paymentMethod);
  }
  if (filter.excludePaymentMethod && filter.excludePaymentMethod.length > 0) {
    filters.notIn('paymentMethod', filter.excludePaymentMethod);
  }
  if (filter.ids) {
    filters.in('id', filter.ids);
  }
  if (filter.excludeIds && filter.excludeIds.length > 0) {
    filters.notIn('id', filter.excludeIds);
  }

  if (filter.documentType && filter.documentType.length > 0) {
    filters.in('document.documentType', filter.documentType);
  }

  // null is the legacy equivalent of NO_ACCOUNT_VALUE
  if (filter.accountId?.includes(NO_ACCOUNT_VALUE) || filter.accountId === null) {
    filters.empty('account.id', true);
  } else if (isDefined(filter.accountId) && !isEmpty(filter.accountId)) {
    filters.in('account.id', filter.accountId);
  }

  if (filter.withoutBankIdentifier) {
    filters.empty('documentCounterpartMissingBankIdentifier', true);
    filters.empty('paymentAccount', true);
  }

  filters.empty('document.hiddenAt', true);

  addCategoryFilter(filters, filter.category || null, 'document.category.id', 'document.subcategory.id');
  return filters;
};

export const fetchAllFlows = async (documentId: string): Promise<Flow[]> => {
  const filterBuilder = jsonAPIFilterBuilder();
  filterBuilder.eq('document.id', documentId);

  const includeBuilder = jsonAPIIncludeBuilder(['account', 'account.consent', 'account.consent.institution']);
  const sortingBuilder = jsonAPISortBuilder([
    {
      sort: 'expected_payment_date',
      direction: 'asc',
    },
    {
      sort: 'id',
      direction: 'desc',
    },
  ]);
  let flows: Flow[] = [];
  let allFlows: Flow[] = [];
  let cursor: MetaDataCursorPage['cursor'] | null = null;
  const pageSize = 25;

  do {
    const pageBuilder = jsonAPICursorPaginatorBuilder({ size: pageSize });
    if (cursor) pageBuilder.cursor(cursor);
    const searchParams = buildJsonAPIQueryParams(pageBuilder, filterBuilder, includeBuilder, sortingBuilder);

    const { data: results } = await backendApiGateway.get<DocumentObject>('/api/v1/flows', { params: searchParams });
    flows = deserialize<Flow[]>(results) as Flow[];
    allFlows = allFlows.concat(flows);
    const { page } = pagedMetaSchema.parse(results.meta);
    cursor = page.cursor;
  } while (flows.length === pageSize);

  return allFlows;
};

export const fetchFlowsMetadata = async (filter: FetchFlowFilters) => {
  const filters = fetchFlowFiltersTojsonAPI(filter);
  const params = buildJsonAPIQueryParams(filters);

  const { data } = await backendApiGateway.get<FlowsTotals>('api/v1/flows/metadata', { params });
  return data;
};

export const bulkFlowUpdate = async (attributes: BulkUpdateFlowPayload, filters: FetchFlowFilters) => {
  const builder = fetchFlowFiltersTojsonAPI(filters);

  const params = buildJsonAPIQueryParams(builder);
  return await backendApiGateway.post<{ num: number }>(
    '/api/v1/flows/bulk-update',
    { attributes, companyId: filters.companyId },
    {
      headers: {
        'content-type': 'application/json',
      },
      params,
    },
  );
};

export const bulkDeleteFlows = async (filters: FetchFlowFilters) => {
  const builder = fetchFlowFiltersTojsonAPI(filters);
  const params = buildJsonAPIQueryParams(builder);
  return await backendApiGateway.post<{ num: number }>(
    '/api/v1/flows/bulk-delete',
    { companyId: filters.companyId },
    {
      headers: { 'content-type': 'application/json' },
      params,
    },
  );
};

export const fetchFlow = async (id: Flow['id']): Promise<FlowWithDocument> => {
  const includeBuilder = jsonAPIIncludeBuilder([
    'account',
    'document',
    'document.flows',
    'reconciliations',
    'document.allocations',
    'document.allocations.category',
    'document.allocations.subcategory',
    'document.allocations.costCenter',
    'document.category',
    'document.subcategory',
    'document.counterpart',
    'payments',
    'email_reminders',
  ]);
  const fieldsBuilder = jsonAPIFieldsBuilder([
    {
      resource: 'reconciliation',
      fields: [],
    },
  ]);

  const searchParams = buildJsonAPIQueryParams(includeBuilder, fieldsBuilder);

  const { data } = await backendApiGateway.get<DocumentObject>(`/api/v1/flows/${id}`, { params: searchParams });

  return deserialize<FlowWithDocument>(data) as FlowWithDocument;
};

export const updateFlow = async ({ id, payloadData }: { id: Flow['id']; payloadData: UpdateFlowValues }) => {
  const payload = serialize(
    {
      id,
      ...payloadData,
      expectedPaymentDate: payloadData.expectedPaymentDate
        ? formatDateForAPI(payloadData.expectedPaymentDate)
        : payloadData.expectedPaymentDate,
      paymentDate: payloadData.paymentDate ? formatDateForAPI(payloadData.paymentDate) : payloadData.paymentDate,
    },
    FlowResourceType,
    { relationships: ['account'] },
  );

  const { data } = await backendApiGateway.patch<DocumentObject>(`/api/v1/flows/${id}`, payload, {
    params: { include: 'account' },
  });

  return deserialize<Flow>(data) as Flow;
};

export const createFlow = async (flow: FlowCreate, companyId: Company['id']) => {
  const payload = serialize(
    {
      ...flow,
      expectedPaymentDate: flow.expectedPaymentDate ? formatDateForAPI(flow.expectedPaymentDate) : null,
      paymentDate: flow.paymentDate ? formatDateForAPI(flow.paymentDate) : null,
      company: { id: companyId },
    },
    FlowResourceType,
    {
      relationships: ['company', 'document', 'account'],
    },
  );

  const { data } = await backendApiGateway.post<DocumentObject>('/api/v1/flows', payload);

  return deserialize<Flow>(data) as Flow;
};

export const deleteFlow = (id: Flow['id']) => backendApiGateway.delete<DocumentObject>(`/api/v1/flows/${id}`);

export const fetchFlowsPageSize = 35;

export const fetchFlows = async (
  filter: FetchFlowFilters,
  sorting: SortInfo<FlowSorting>,
  pagination: Partial<PaginationInfo>,
) => {
  const filters = fetchFlowFiltersTojsonAPI(filter);
  const page = jsonAPICursorPaginatorBuilder({
    size: pagination.size ?? fetchFlowsPageSize,
    cursor: pagination.cursor,
  });
  const include = jsonAPIIncludeBuilder([
    'company',
    'document',
    'document.flows',
    'document.flows.bookkeepingEntry',
    'account',
    'document.allocations',
    'document.allocations.category',
    'document.allocations.subcategory',
    'document.allocations.costCenter',
    'document.category',
    'document.subcategory',
    'document.counterpart',
    'payments',
  ]);
  const sort = jsonAPISortBuilder([
    sorting,
    { sort: 'paymentDate', direction: 'asc' },
    { sort: 'createdAt', direction: 'asc' },
    { sort: 'id', direction: 'asc' },
  ]);

  const params = buildJsonAPIQueryParams(filters, page, include, sort);

  const { data } = await backendApiGateway.get<DocumentObject & { meta: FlowsMeta }>('api/v1/flows', { params });
  const result = deserialize<FlowWithDocument>(data) as FlowWithDocument[];
  return { data: result, meta: data.meta as FlowsMeta };
};

const fetchFlowsReconciliationProposal = async (filters: { ids: Flow['id'][]; paymentStatus?: FlowPaymentStatus }) => {
  const apiFilters = fetchFlowFiltersTojsonAPI(filters);
  const params = buildJsonAPIQueryParams(apiFilters);

  const { data } = await backendApiGateway.get<
    {
      flow_id: string;
      transaction_ids: string[];
    }[]
  >('/api/v1/flows/reconciliations', { params });

  return filters.ids.map((x) => {
    const flows = data.find((y) => y.flow_id === x)?.transaction_ids ?? [];
    return { flowId: x, transactionIds: flows };
  });
};

export const reconciliations = create({
  fetcher: (ids: Flow['id'][]) => fetchFlowsReconciliationProposal({ ids, paymentStatus: FlowPaymentStatus.ToPay }),
  resolver: keyResolver('flowId'),
  scheduler: windowedFiniteBatchScheduler({
    windowMs: 50,
    maxBatchSize: 50,
  }),
});

export const reconciliationsForced = create({
  fetcher: (ids: Flow['id'][]) => fetchFlowsReconciliationProposal({ ids }),
  resolver: keyResolver('flowId'),
  scheduler: windowedFiniteBatchScheduler({
    windowMs: 50,
    maxBatchSize: 50,
  }),
});

export const sendMailReminder = async ({
  to,
  from,
  subject,
  body,
  flowsId,
}: {
  to: string[];
  from: string;
  subject: string;
  body: string;
  flowsId: Flow['id'][];
}) => {
  const data = { to, subject, flow_id: flowsId, from, body };
  await backendApiGateway.post<DocumentObject & { meta: FlowsMeta }>('api/v1/flows/send-mail-reminder', data);
};

export const splitPayment = async ({
  actionType,
  sourceFlowId,
  data,
}:
  | {
      actionType: 'new-flow';
      sourceFlowId: Flow['id'];
      data: SplitPaymentNewFlowRequest;
    }
  | {
      actionType: 'move-to-existing';
      sourceFlowId: Flow['id'];
      data: SplitPaymentMoveToExistingRequest;
    }) => {
  await backendApiGateway.post<SplitPaymentMoveToExistingRequest>(
    `api/v1/flows/${sourceFlowId}/split-payment/${actionType}`,
    data,
  );
};
